import { Box, Button, Icon, Stack, Typography, useTheme } from '@mui/material';
import Image from 'next/image';
import { getIconForStatus, showConnectingLineForStatus } from '../helpers';
import ChevronDown from 'mdi-material-ui/ChevronDown';
import { useEffect, useState } from 'react';
import { UIStatusGroup, UIStatusGroupItem } from '../types';
import { Alert, CalendarBlank, ClockOutline } from 'mdi-material-ui';
import { format, parseISO } from 'date-fns';
import { VigoTypography } from 'src/@vigo/vigo-typography';
import { StatusPanelItems } from '..';

interface StatusDetailsProps {
  status: UIStatusGroup;
  currentlyActiveStatusId: string;
  orderItems?: StatusPanelItems[];
  overrideComplete?: boolean;
}

const StatusDetails = ({
  status,
  currentlyActiveStatusId,
  orderItems,
  overrideComplete = false
}: StatusDetailsProps) => {
  const [itemDetailsOpen, setItemDetailsOpen] = useState<boolean>(false);
  const [filteredItems, setFilteredItems] = useState<UIStatusGroupItem[]>([]);

  const theme = useTheme();

  const formatItemNo = (itemNumber: number | undefined) => {
    if (!itemNumber) return '';

    return itemNumber < 10 ? `0${itemNumber}` : itemNumber;
  };

  function filterStatusItems() {
    const newItems: UIStatusGroupItem[] = [];
    const palletLevelItems: string[] = [];

    for (let index = 0; index < status.items.length; index++) {
      const item = status.items[index];

      if (item.salesOrderItemId) {
        const itemNo = orderItems?.find(x => x.id == item.salesOrderItemId)?.itemNo;
        const statusCode = item.code;

        if (itemNo) {
          // Has a status with the same code and itemNo been shown before?
          if (palletLevelItems.includes(`${statusCode}-${itemNo}`)) {
            // Don't show the item
            continue;
          } else {
            // Show the item
            palletLevelItems.push(`${statusCode}-${itemNo}`);
            newItems.push(item);
          }
        } else {
          // Show the item
          newItems.push(item);
        }
      } else {
        // Add the item to the newItems array
        newItems.push(item);
      }
    }

    setFilteredItems(newItems);
  }

  useEffect(() => {
    filterStatusItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const StatusItem = ({ item }: { item: UIStatusGroupItem }) => {
    return (
      <Stack key={item.itemId} direction="column">
        <Stack
          direction="row"
          width="100%"
          gap="15px"
          justifyContent="space-between"
          alignItems="center"
          sx={{ padding: '5px', paddingBottom: '0px' }}>
          <VigoTypography
            value={
              item.salesOrderItemId
                ? `Pallet ${formatItemNo(orderItems?.find(x => x.id == item.salesOrderItemId)?.itemNo)} - (${
                    item?.code ?? ''
                  }) ${item.description}`
                : `(${item?.code ?? ''}) ${item?.description ?? ''}`
            }
            disableTranslation
            sx={{ fontSize: '0.9rem' }}
          />

          <Stack direction="row" gap="10px" justifyContent="center" alignItems="center">
            <Stack direction="row" gap={2} justifyContent="center" alignItems="center">
              <CalendarBlank sx={{ width: '16px', height: '16px' }} />
              <VigoTypography
                value={item?.statusDate ? format(parseISO(item.statusDate as string), 'dd/MM/yy') : ''}
                sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}
                disableTranslation
              />
            </Stack>

            <Stack direction="row" gap={2} justifyContent="center" sx={{ width: '80px' }}>
              <ClockOutline sx={{ width: '16px', height: '16px', mt: '2px' }} />
              <VigoTypography
                value={item?.statusDate ? format(parseISO(item.statusDate as string), 'HH:mm') : ''}
                sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}
                disableTranslation
              />
            </Stack>
          </Stack>
        </Stack>
        
        {(item?.podName ?? '')?.length > 0 && (
          <Stack direction="row" gap="10px" sx={{ paddingLeft: '10px', paddingBottom: '2px' }}>
            <VigoTypography
              value="- Signed:"
              sx={{ fontWeight: 'bold', fontSize: '0.857rem', flexShrink: 0 }}
            />
            <VigoTypography disableTranslation value={item.podName} sx={{ fontSize: '0.857rem', flex: 1 }} />
          </Stack>
        )}        

        {/* Status Notes */}
        {(item?.notes ?? '')?.length > 0 && (
          <Stack direction="row" gap="10px" sx={{ paddingLeft: '10px', paddingBottom: '2px' }}>
            <VigoTypography
              value="- Notes:"
              sx={{ fontWeight: 'bold', fontSize: '0.857rem', flexShrink: 0 }}
            />
            <VigoTypography disableTranslation value={item.notes} sx={{ fontSize: '0.857rem', flex: 1 }} />
          </Stack>
        )}
      </Stack>
    );
  };

  return (
    <Stack
      direction="row"
      gap="10px"
      sx={{
        height: '100%',
        width: '100%',
        padding: '0px !important'
      }}>
      {/* Icon section */}
      <Stack direction="column" alignItems="center" gap="5px" sx={{ flexShrink: 0, padding: '0px !important' }}>
        {status.code === 'Discrep' ? (
          <Icon
            sx={{
              width: '34px',
              height: '34px',
              display: 'flex',
              justifyContent: 'center',
              alignContent: 'center',
              color: status.iconColour
            }}>
            <Alert />
          </Icon>
        ) : (
          <>
            <Image
              src={getIconForStatus(
                status?.code ?? '',
                Boolean(status?.advisories?.length ? true : false),
                Boolean(status?.warnings?.length ? true : false),
                Boolean(status?.date ? true : false),
                currentlyActiveStatusId === status.id,
                overrideComplete
              )}
              alt="status icon"
              width={34}
              height={34}
              style={{ flexShrink: 0, flexBasis: '34px' }}
            />
            {status.showLine && (
              <Box
                sx={{
                  position: 'absolute',
                  marginTop: '40px',
                  width: '2px',
                  height: '27px',
                  backgroundColor: '#b3b3b3'
                }}
              />
            )}
          </>
        )}

        {showConnectingLineForStatus(status) && (
          <Box
            sx={{
              flex: 1,
              borderLeft: `3px solid ${status.code === 'Discrep' ? status.iconColour : '#32CD32'}`
            }}></Box>
        )}
      </Stack>

      {/* Status & details */}
      <Stack
        direction="column"
        sx={
          status.jsxElement
            ? {
                width: '100%'
              }
            : {
                paddingBlock: '5px 10px',
                width: '100%'
              }
        }>
        <Stack direction="row">
          {status.jsxElement ? (
            <status.jsxElement />
          ) : (
            <VigoTypography
              sx={{
                color: theme.palette.mode === 'light' ? '#2B2D42' : '#fff',
                fontSize: '1rem',
                fontWeight: 'bold'
              }}
              value={status.description}
            />
          )}

          {status.items.length > 0 && (
            <>
              <Typography
                component="small"
                sx={{
                  fontSize: '0.786rem',
                  marginLeft: '5px',
                  marginTop: '2px'
                }}>{`(${status.items.length})`}</Typography>
              <Button
                size="small"
                endIcon={
                  <ChevronDown
                    sx={{
                      transition: 'transform .3s ease',
                      color: theme.palette.mode === 'light' ? '#2B2D42' : '#ECECF0',
                      ...(itemDetailsOpen && { transform: 'rotate(180deg)' })
                    }}
                  />
                }
                onClick={() => setItemDetailsOpen(prev => !prev)}
              />
            </>
          )}
        </Stack>
        <Stack
          direction="column"
          sx={{
            width: '100%',
            overflowY: 'auto',
            maxHeight: itemDetailsOpen ? '150px' : 0,
            transition: 'max-height 0.3s linear, border-color 0.3s linear'
          }}>
          {filteredItems.map(item => (
            <StatusItem key={item.itemId} item={item} />
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default StatusDetails;
